import { useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Gift, Check, Loader2, X } from "lucide-react";

/**
 * PromoCodeInput - Reusable component for entering and redeeming promo codes
 */
export default function PromoCodeInput({ onSuccess, className = "" }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(null); // { valid, tier } or null
  const [error, setError] = useState("");

  const validateCode = async () => {
    if (!code.trim()) return;
    
    setLoading(true);
    setError("");
    setValidated(null);
    
    try {
      const res = await api.get(`/promo/validate/${code.toUpperCase()}`);
      if (res.data.valid) {
        setValidated({ valid: true, tier: res.data.tier });
      } else {
        setError(res.data.error || "Invalid code");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to validate code");
    } finally {
      setLoading(false);
    }
  };

  const redeemCode = async () => {
    if (!validated?.valid) return;
    
    setLoading(true);
    try {
      const res = await api.post("/promo/redeem", { code: code.toUpperCase() });
      toast.success(res.data.message || "Promo code applied!");
      onSuccess?.(res.data.tier);
      setCode("");
      setValidated(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to redeem code");
    } finally {
      setLoading(false);
    }
  };

  const clearCode = () => {
    setCode("");
    setValidated(null);
    setError("");
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 text-sm text-indigo-400">
        <Gift className="w-4 h-4 text-indigo-400" />
        Have a promo code?
      </div>
      
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setValidated(null);
              setError("");
            }}
            placeholder="ENTER CODE"
            disabled={validated?.valid}
            className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none uppercase disabled:opacity-50 pr-10"
            data-testid="promo-code-input"
          />
          {validated?.valid && (
            <button
              onClick={clearCode}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-500"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {!validated?.valid ? (
          <button
            onClick={validateCode}
            disabled={loading || !code.trim()}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="validate-promo-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Check"
            )}
          </button>
        ) : (
          <button
            onClick={redeemCode}
            disabled={loading}
            className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="redeem-promo-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Check className="w-4 h-4" />
                Apply
              </>
            )}
          </button>
        )}
      </div>
      
      {error && (
        <p className="text-red-400 text-xs">{error}</p>
      )}
      
      {validated?.valid && (
        <p className="text-green-400 text-xs flex items-center gap-1">
          <Check className="w-3 h-3" />
          Valid! This code gives you <span className="font-semibold">{validated.tier?.toUpperCase()}</span> access.
        </p>
      )}
    </div>
  );
}
